import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { SegmentsTable } from "@/components/configuration/SegmentsTable";
import { Button } from "@/components/generic/Button";
import {
  getConfigurationCounts,
  getConfigurationSuggestions,
} from "@/queries/configurations";
import { Configuration, ConfigurationSuggestion } from "@/types/campaign";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { SetStateAction, useCallback, useMemo } from "react";

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/define/advanced/",
)({
  component: RouteComponent,
  pendingComponent: () => (
    <div className="flex flex-col items-center justify-center gap-y-4">
      <Loader2 size={32} className="animate-spin" />
      <div className="animate-pulse">Indlæser segmenter...</div>
    </div>
  ),
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(
      context.configurationSuggestionsQueryOptions,
    );
  },
});

const linkContext = {
  editFilter: (category: string, filterId: string, quality: number) =>
    linkOptions({
      from: Route.fullPath,
      to: "/campaigns/$id/define/advanced/$quality/$category/$filter",
      params: { quality: quality, category, filter: filterId },
    }),
  contacts: (quality: number) =>
    linkOptions({
      from: Route.fullPath,
      to: "/campaigns/$id/define/advanced/$quality/$category/contacts",
      params: { category: "contacts", quality: quality },
    }),
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const { useAdvancedConfigurations, useEditCampaign } =
    Route.useRouteContext();
  const { newConfigurations, setNewConfigurations, isDirty } =
    useAdvancedConfigurations();
  const { editCampaign, setEditCampaign } = useEditCampaign();

  const goldConfig = newConfigurations.find((c) => c.quality === 1);

  // Re-use the cached suggestion data (staleTime: Infinity in the query def).
  // The /suggest response includes counts for ALL quality segments, even
  // disabled ones, unlike /counts which only covers enabled segments.
  const { data: suggestionsData } = useQuery(
    getConfigurationSuggestions({
      configuration: goldConfig,
      campaignId: id,
      stopListIds:
        editCampaign.stopLists?.map((s) => s.uniqueId.toString()) ?? [],
    }),
  );

  const getSegmentsTableConfigurations = (
    configurations: Configuration[],
    suggestions?: ConfigurationSuggestion[],
  ) => {
    return configurations.map((config) => {
      if (config.enabled) return config;
      const foundSuggestion = suggestions?.find(
        (s) => s.configuration.quality === config.quality,
      );
      if (!foundSuggestion) return config;
      return {
        ...foundSuggestion.configuration,
        id: config.id,
      };
    });
  };

  const segmentsTableConfigurations = useMemo(() => {
    return getSegmentsTableConfigurations(newConfigurations, suggestionsData);
  }, [newConfigurations, suggestionsData]);

  const setSegmentsTableConfigurations = useCallback(
    (action: SetStateAction<Configuration[]>) => {
      setNewConfigurations((prev) => {
        const segmentsTableConfigurations = getSegmentsTableConfigurations(
          prev,
          suggestionsData,
        );
        return typeof action === "function"
          ? action(segmentsTableConfigurations)
          : action;
      });
    },
    [setNewConfigurations, suggestionsData],
  );

  // Enabled segments keep their own filters instead of the suggestion, so their
  // counts must come from /counts rather than the suggested configuration's count.
  const { data: countsData } = useQuery(
    getConfigurationCounts({
      configurations: segmentsTableConfigurations,
      stopListIds:
        editCampaign.stopLists?.map((s) => s.uniqueId.toString()) ?? [],
      campaignId: editCampaign.id,
    }),
  );

  const externalCounts = useMemo(() => {
    if (!suggestionsData || !countsData) return undefined;
    const countsByQuality = new Map(
      countsData.map(({ quality, count }) => [quality, count]),
    );
    const enabledQualities = new Set(
      segmentsTableConfigurations
        .filter((c) => c.enabled && !c.deleted)
        .map((c) => c.quality),
    );
    return new Map(
      suggestionsData.map((s) => {
        const quality = s.configuration.quality;
        return [
          quality,
          enabledQualities.has(quality)
            ? (countsByQuality.get(quality) ?? 0)
            : s.count,
        ];
      }),
    );
  }, [suggestionsData, countsData, segmentsTableConfigurations]);

  const handleSave = () => {
    setEditCampaign((prev) => ({
      ...prev,
      configurations: segmentsTableConfigurations,
    }));
    navigate({ to: "/campaigns/$id/define", ignoreBlocker: true });
  };

  const handleCancel = () => {
    navigate({ to: "/campaigns/$id/define" });
  };

  return (
    <div className="flex flex-col gap-y-10">
      <SegmentHeader
        className="mx-auto max-w-122"
        title="Avanceret"
        description="Udforsk strategiske variationer af din målgruppe. Nedenfor ser du forslag til hvordan du kan variere målgruppen, og derved udvide din målgruppe."
      />

      <SegmentsTable
        configurations={segmentsTableConfigurations}
        setConfigurations={setSegmentsTableConfigurations}
        linkContext={linkContext}
        externalCounts={externalCounts}
        stopLists={editCampaign.stopLists ?? []}
        campaignId={editCampaign.id}
      />

      <div className="flex justify-center gap-6">
        <Button
          type="button"
          intent="secondary"
          className="h-12 min-w-37.5"
          animate
          onPress={handleCancel}
        >
          Annuller
        </Button>
        <Button
          type="button"
          className="h-12 min-w-37.5"
          animate
          isDisabled={!isDirty}
          onPress={handleSave}
        >
          Gem ændringer
        </Button>
      </div>
    </div>
  );
}
